import { useStore } from "@/core/services/store";
import { setXentralConfig } from "@/composables/useSalesChannelManagementApp";
import { isEmpty } from "lodash";
import { computed } from "vue";
import { Toast } from "@/core/plugins/swal";
import i18n from "@/core/plugins/vue-i18n";
import { baseUrl } from "@/components/ExternalApps/SalesChannelManagementApp/config/apps/bexioApp";
import { useApp } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useApp";
import useOauth from "@/components/ExternalApps/SalesChannelManagementApp/composables/useOauth";

export const useBexioOauth = () => {
  const store = useStore();
  const { base64ToUtf8, utf8ToBase64 } = useOauth();
  const { selectIntegration } = useApp();

  const xentralConfigData = computed(
    () => store.getters.xentralConfig?.data ?? []
  );
  const queryState = computed(() =>
    base64ToUtf8(xentralConfigData.value?.["state"])
  );

  // Renews the token from the settings area. `isReauthorization` makes the core
  // store the token on the existing integration and redirect back to
  // `original_from_url` as-is, which carries the `fetched` state so App.vue
  // re-opens the settings instead of the wizard. `from_url` must be set for the
  // core to pick that branch.
  async function getAccessToken() {
    const xentralConfig = store.getters.xentralConfig?.data;
    const fromUrl = xentralConfig?.baseUrl?.replace(/\/+$/, "") || "";
    const endpoint = xentralConfig?.xentralAppUri || "";
    const target = `${fromUrl}/${endpoint.replace(/^\/+/, "")}`;
    const projectId = store.getters?.selectedIntegration?.id;
    const redirectUri = store.getters.oauthVariable("redirect_uri");

    const returnState = encodeURIComponent(
      utf8ToBase64({ fetched: true, project_id: projectId })
    );
    const separator = target.includes("?") ? "&" : "?";

    const state = {
      licenseId: store.getters.licenseId,
      isReauthorization: true,
      project_id: projectId,
      selectedAppName: "bexio",
      from_url: redirectUri,
      original_from_url: `${target}${separator}state=${returnState}`
    };

    const params = new URLSearchParams({
      client_id: store.getters.oAuthClientId,
      redirect_uri: redirectUri,
      response_type: store.getters.oauthVariable("response_type"),
      scope: store.getters.oauthVariable("scope"),
      state: utf8ToBase64(state)
    });

    resetXentralConfig();

    window.open(`${baseUrl}?${params}`, "_parent");
  }

  // Called from App.vue after returning from bexio consent: re-select the
  // integration the token was generated for and drop the user back into its
  // settings, mirroring the amazon/ebay reauthorize flow.
  async function applyBexioOAuthValues() {
    if (isEmpty(queryState.value)) return;

    const selectedIntegration = store.getters.integrationProject(
      queryState.value.project_id
    );
    await selectIntegration(
      selectedIntegration,
      "settings",
      "settings",
      "wizard"
    );

    await Toast.fire({
      icon: "success",
      title: i18n.t("salesChannelManagementApp.newTokenSuccess")
    });

    resetXentralConfig();
  }

  function resetXentralConfig() {
    //Remove state property from xentralConfig vuex store
    let xentralData = store.getters.xentralConfig;

    // Create a new object excluding the 'state' property
    let newData = { ...xentralData.data };
    delete newData.state;

    let updatedXentralConfig = { ...xentralData, data: newData };

    setXentralConfig(updatedXentralConfig);
  }

  return {
    getAccessToken,
    applyBexioOAuthValues
  };
};
